import { useState } from 'react'

import { JourneyFrame, Note, SourceChip } from './JourneyChrome'
import {
  type Attempt,
  HELP_LABELS,
  type SampleMaterial,
  type SelfReport,
  type Topic,
} from './model'
import {
  questionOriginLabel,
  type NextActionKind,
  observedLine,
  patternLine,
  summarizeRun,
} from './report'
import {
  DEMO_TEXT,
  NO_MASTERY_TEXT,
  PANEL,
  PRIMARY,
  SECONDARY,
  SELF_REPORT_TEXT,
  STATUS_TEXT,
} from './ui'

/**
 * Step 6: what happened in this practice, kept in three visibly separate
 * layers (D-14.4): observed facts (answers, correct or not, help used), a
 * DEMO interpretation (repeated error patterns, evidence status) and one next
 * action. It never claims mastery: a few answers cannot show it.
 */

export interface ResultStepProps {
  readonly sample: SampleMaterial
  readonly topic: Topic
  readonly attempts: readonly Attempt[]
  readonly selfReport: SelfReport | null
  readonly onAction: (kind: NextActionKind) => void
  readonly onBack: () => void
  readonly onHome: () => void
}

export function ResultStep({
  sample,
  topic,
  attempts,
  selfReport,
  onAction,
  onBack,
  onHome,
}: ResultStepProps): React.JSX.Element {
  const summary = summarizeRun(topic, attempts)
  const tiles = [
    {
      label: 'Đã làm',
      value: `${String(summary.attempted)}/${String(summary.total)}`,
      tone: 'bg-sky-50 dark:bg-sky-950/50',
    },
    {
      label: 'Đúng',
      value: String(summary.correct),
      tone: 'bg-emerald-50 dark:bg-emerald-950/50',
    },
    {
      label: 'Chưa đúng',
      value: String(summary.incorrect),
      tone: 'bg-amber-100 dark:bg-amber-950/50',
    },
    {
      label: 'Có trợ giúp',
      value: String(summary.assisted),
      tone: 'bg-slate-100 dark:bg-slate-800',
    },
  ]

  return (
    <JourneyFrame
      step="Kết quả"
      title="Kết quả luyện tập"
      onBack={onBack}
      onHome={onHome}
      demo
    >
      <Note>{DEMO_TEXT.result}</Note>
      <SourceChip sample={sample} />

      <section aria-labelledby="result-facts" className="flex flex-col gap-3">
        <h2 id="result-facts" className="text-xl font-bold">
          Con đã làm
        </h2>
        <ul className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {tiles.map((t) => (
            <li
              key={t.label}
              className={`flex flex-col items-center rounded-2xl p-3 ${t.tone}`}
            >
              <span className="text-3xl font-bold">{t.value}</span>
              <span className="text-base">{t.label}</span>
            </li>
          ))}
        </ul>
        <ol aria-label="Từng câu" className="flex flex-col gap-2">
          {topic.questions.map((q, i) => {
            const attempt = attempts.find((a) => a.questionId === q.id)
            return (
              <AnswerRow
                key={q.id}
                n={i + 1}
                prompt={q.prompt}
                lang={q.lang}
                explanation={q.explanation}
                source={questionOriginLabel(sample, q)}
                attempt={attempt ?? null}
              />
            )
          })}
        </ol>
        {selfReport !== null && (
          <p className="text-base text-slate-700 dark:text-slate-300">
            Con tự đánh giá sau phần học: “{SELF_REPORT_TEXT[selfReport]}” (con tự nói,
            không phải kết quả bài làm).
          </p>
        )}
      </section>

      <section aria-labelledby="result-interpretation" className={PANEL}>
        <h2 id="result-interpretation" className="text-xl font-bold">
          Nhận xét
        </h2>
        <p className="text-lg font-semibold">{STATUS_TEXT[summary.status]}</p>
        <p className="text-base">{observedLine(summary)}</p>
        <p className="text-base text-slate-700 dark:text-slate-300">
          Chỉ câu tự làm (không mở trợ giúp) được dùng để nhận xét.
        </p>
        {summary.patterns.length > 0 && (
          <ul aria-label="Kiểu lỗi" className="flex flex-col gap-1">
            {summary.patterns.map((p) => (
              <li key={p.tag} className="text-base">
                {patternLine(topic, p)}
              </li>
            ))}
          </ul>
        )}
        <p className="text-base text-slate-700 dark:text-slate-300">{NO_MASTERY_TEXT}</p>
        <Note>Nhận xét theo quy tắc soạn sẵn cho bài mẫu. Không phải AI đang chạy.</Note>
      </section>

      <section
        aria-labelledby="result-next"
        className="flex flex-col gap-3 rounded-3xl border-2 border-sky-300 bg-sky-50 p-4 dark:border-sky-800 dark:bg-sky-950/40"
      >
        <h2 id="result-next" className="text-xl font-bold">
          <span aria-hidden>👉 </span>Việc nên làm tiếp
        </h2>
        <p className="text-lg font-semibold">{summary.nextAction.text}</p>
        <p className="text-base text-slate-700 dark:text-slate-300">
          Vì: {summary.nextAction.reason}
        </p>
        <button
          type="button"
          className={PRIMARY}
          onClick={() => {
            onAction(summary.nextAction.kind)
          }}
        >
          {ACTION_LABEL[summary.nextAction.kind]}
        </button>
        <div className="flex flex-col gap-2 md:flex-row md:flex-wrap">
          {OTHER_ACTIONS.filter((a) => a !== summary.nextAction.kind).map((kind) => (
            <button
              key={kind}
              type="button"
              className={SECONDARY}
              onClick={() => {
                onAction(kind)
              }}
            >
              {ACTION_LABEL[kind]}
            </button>
          ))}
        </div>
      </section>

      <button type="button" className={`${SECONDARY} md:self-center`} onClick={onHome}>
        Về trang chính
      </button>
    </JourneyFrame>
  )
}

const ACTION_LABEL: Readonly<Record<NextActionKind, string>> = {
  review_explanation: 'Xem lại bài học',
  practise_again: 'Luyện lại',
  try_new_material: 'Chọn tài liệu khác',
}

const OTHER_ACTIONS: readonly NextActionKind[] = [
  'review_explanation',
  'practise_again',
  'try_new_material',
]

function AnswerRow({
  n,
  prompt,
  lang,
  explanation,
  source,
  attempt,
}: {
  n: number
  prompt: string
  lang: 'en' | undefined
  explanation: string
  source: string
  attempt: Attempt | null
}): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const id = `answer-detail-${String(n)}`
  const mark = attempt === null ? '–' : attempt.correct ? '✓' : '✗'
  const state = attempt === null ? 'Chưa làm' : attempt.correct ? 'Đúng' : 'Chưa đúng'

  return (
    <li className="flex flex-col gap-1 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={`flex size-8 shrink-0 items-center justify-center rounded-full text-lg font-bold ${
            attempt?.correct === true
              ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100'
              : attempt === null
                ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                : 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100'
          }`}
        >
          {mark}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-lg">
            <span className="sr-only">
              Câu {n}, {state}:{' '}
            </span>
            <span lang={lang}>{prompt}</span>
          </p>
          {attempt !== null && (
            <p className="text-base text-slate-700 dark:text-slate-300">
              Con trả lời: <span className="font-semibold">{attempt.display}</span>
              {!attempt.correct && (
                <>
                  {' '}
                  · Đáp án:{' '}
                  <span className="font-semibold">{attempt.correctDisplay}</span>
                </>
              )}
            </p>
          )}
          {attempt !== null && attempt.helpBeforeSubmit.length > 0 && (
            <p className="text-base text-slate-700 dark:text-slate-300">
              Có trợ giúp:{' '}
              {attempt.helpBeforeSubmit.map((m) => HELP_LABELS[m]).join(', ')}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        className="min-h-11 self-start px-1 text-base text-sky-800 underline dark:text-sky-300"
        onClick={() => {
          setOpen((v) => !v)
        }}
      >
        {open ? 'Ẩn giải thích' : 'Xem giải thích'}
      </button>
      <div id={id} hidden={!open} className="text-base">
        {attempt?.mistake != null && <p>{attempt.mistake.feedback}</p>}
        <p>{explanation}</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">{source}</p>
      </div>
    </li>
  )
}
